import { Injectable } from '@angular/core';

/**
 * Identificación de camarero SIN conexión (bloqueo, cambio de camarero, pulsera).
 *
 * El PIN y la pulsera los resuelve el servidor (model/staffpin.php): el cliente no
 * tiene los hashes del modelo. Sin red no se podría entrar, así que cada identificación
 * correcta CON red deja en ESTE dispositivo una huella lenta de la credencial (PBKDF2
 * con sal propia del dispositivo), nunca la credencial. Sin red se compara con esa
 * huella. Sirve para quien se haya identificado aquí con red alguna vez: no caduca.
 *
 * Límites asumidos: un PIN cambiado sigue valiendo sin red hasta que ese camarero se
 * identifique aquí con el nuevo o el servidor rechace el antiguo; un camarero dado de
 * baja, hasta que el modelo del local se actualice; y un PIN corto se puede sacar de la
 * huella con acceso al dispositivo. Por eso se borra al cerrar sesión.
 */
type Credential = { pin?: string, rfid?: string };

interface StoredEntry {
    user: any;
    kind: 'pin' | 'rfid';
    hash: string;
    at: number;                 // última identificación con red
}

interface StoredIdent {
    salt: string;
    places: { [place: string]: StoredEntry[] };
}

const STORAGE_KEY = 'unpispas_waiter_offline_ident';
const ITERATIONS  = 150000;
const MAX_FAILS   = 5;                  // PIN incorrectos seguidos sin red antes de la espera
const BLOCK_MS    = 60 * 1000;          // el servidor no puede limitar los intentos sin red

@Injectable({ providedIn: 'root' })
export class waiterOfflineIdentService {
    private _fails = 0;
    private _blockedUntil = 0;
    private _generation = 0;            // sube al borrar: lo que estaba derivando ya no escribe

    /** Identificación correcta CON red: recuerda la credencial en este dispositivo. */
    async Remember(place: any, user: any, credential: Credential): Promise<void> {
        try {
            // La sal se fija ANTES de derivar: dos identificaciones a la vez usan la misma.
            let _data = this._read();
            if (!_data){
                _data = { salt: this._newSalt(), places: {} };
                this._write(_data);
            }
            let _salt = _data.salt;
            let _generation = this._generation;
            let _hash = await this._derive(_salt, place, credential);
            _data = this._current(_salt, _generation);
            if (!_hash || !_data){
                return;
            }
            let _kind = credential.rfid ? 'rfid' : 'pin';
            let _entries = this._alive(_data.places[String(place)]).filter(e => (e.hash !== _hash) && ((String(e.user) !== String(user)) || (e.kind !== _kind)));
            _entries.push({ user: user, kind: _kind as ('pin' | 'rfid'), hash: _hash, at: Date.now() });
            _data.places[String(place)] = _entries;
            this._write(_data);
        }
        catch(e){
            console.warn('[WAITER-IDENT] no se pudo recordar la identificación', e);
        }
    }

    /** Sin red: objid del USER con esa credencial en este dispositivo, o null. */
    async Find(place: any, credential: Credential): Promise<any> {
        let _isPin = !credential.rfid;
        if (_isPin && (Date.now() < this._blockedUntil)){
            return null;
        }
        let _user = null;
        try {
            let _data = this._read();
            let _entries = _data ? this._alive(_data.places[String(place)]) : [];
            let _hash = _entries.length ? await this._derive(_data.salt, place, credential) : null;
            let _found = _hash ? _entries.find(e => e.hash === _hash) : null;
            _user = _found ? _found.user : null;
        }
        catch(e){
            console.warn('[WAITER-IDENT] no se pudo comprobar la identificación', e);
        }
        // Solo el PIN cuenta: una pulsera desconocida puede ser un código de barras.
        if (_isPin){
            this._fails = _user ? 0 : (this._fails + 1);
            if (this._fails >= MAX_FAILS){
                this._fails = 0;
                this._blockedUntil = Date.now() + BLOCK_MS;
            }
        }
        return _user;
    }

    /** El servidor ha rechazado esta credencial: ya no vale tampoco sin red. */
    async Forget(place: any, credential: Credential): Promise<void> {
        try {
            let _data = this._read();
            if (!_data || !_data.places[String(place)]){
                return;
            }
            let _salt = _data.salt;
            let _generation = this._generation;
            let _hash = await this._derive(_salt, place, credential);
            _data = this._current(_salt, _generation);
            if (!_hash || !_data){
                return;
            }
            _data.places[String(place)] = this._alive(_data.places[String(place)]).filter(e => e.hash !== _hash);
            this._write(_data);
        }
        catch(e){
            console.warn('[WAITER-IDENT] no se pudo olvidar la identificación', e);
        }
    }

    /** Al cerrar sesión no queda nada en el dispositivo. */
    Clear(){
        this._generation++;
        try {
            localStorage.removeItem(STORAGE_KEY);
        }
        catch(e){}
    }

    // Lo guardado tras derivar, o null si mientras tanto se borró (aquí o en otra ventana):
    // escribir restauraría lo que el cierre de sesión acaba de eliminar.
    private _current(salt: string, generation: number): StoredIdent {
        let _data = this._read();
        return ((generation === this._generation) && _data && (_data.salt === salt)) ? _data : null;
    }

    private _alive(entries: StoredEntry[]): StoredEntry[] {
        return (Array.isArray(entries) ? entries : []).filter(e => e && (typeof e.hash === 'string'));
    }

    // crypto.subtle solo existe en contexto seguro: sin él no se recuerda nada.
    private async _derive(salt: string, place: any, credential: Credential): Promise<string> {
        let _subtle = (typeof crypto !== 'undefined') ? crypto.subtle : null;
        if (!_subtle){
            return null;
        }
        let _kind = credential.rfid ? 'rfid' : 'pin';
        let _encoder = new TextEncoder();
        let _key = await _subtle.importKey('raw', _encoder.encode(String(place) + '|' + _kind + '|' + (credential.rfid || credential.pin)), 'PBKDF2', false, [ 'deriveBits' ]);
        let _bits = await _subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: _encoder.encode(salt), iterations: ITERATIONS }, _key, 256);
        return btoa(String.fromCharCode(...Array.from(new Uint8Array(_bits))));
    }

    private _newSalt(): string {
        return btoa(String.fromCharCode(...Array.from(crypto.getRandomValues(new Uint8Array(16)))));
    }

    private _read(): StoredIdent {
        try {
            let _raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
            return (_raw && (typeof _raw.salt === 'string') && _raw.places && (typeof _raw.places === 'object')) ? _raw : null;
        }
        catch(e){
            return null;
        }
    }

    private _write(data: StoredIdent){
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
}
